/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-84 — Code 128, hand-encoded into dots
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * WHY NOT A LIBRARY. The printer path is a 1-bit raster (`catPrinter.ts`), and
 * a barcode drawn on a canvas by a library at a non-integer module width comes
 * out with uneven bars once `toMonoBitmap` thresholds it — unreadable at 203
 * dpi. Emitting the bar widths ourselves and painting them as integer-dot
 * rectangles puts every edge exactly on a dot. It is also ~150 lines, pure,
 * and testable in jest — and the test DECODES the output through the zxing
 * reader the kiosk scanner already uses, so the symbol table is verified, not
 * trusted.
 *
 * WHY CODE 128. The kiosk scanner reads it (`BarcodeScanner.tsx`) and reads no
 * QR; it is alphanumeric, so `BX000017` can never be mistaken for an EAN; and
 * code set C packs the digit run two per symbol, which is what lets a label
 * fit a 58 mm head at 3 dots per module.
 */

/** Bar/space module widths for symbols 0..105, and the 7-element STOP (106). */
const PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312",
  "132212", "221213", "221312", "231212", "112232", "122132", "122231", "113222",
  "123122", "123221", "223211", "221132", "221231", "213212", "223112", "312131",
  "311222", "321122", "321221", "312212", "322112", "322211", "212123", "212321",
  "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121",
  "313121", "211331", "231131", "213113", "213311", "213131", "311123", "311321",
  "331121", "312113", "312311", "332111", "314111", "221411", "431111", "111224",
  "111422", "121124", "121421", "141122", "141221", "112214", "112412", "122114",
  "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112",
  "421211", "212141", "214121", "412121", "111143", "111341", "131141", "114113",
  "114311", "411113", "411311", "113141", "114131", "311141", "411131", "211412",
  "211214", "211232", "2331112",
];

const CODE_C = 99;
const CODE_B = 100;
const START_B = 104;
const START_C = 105;
const STOP = 106;

export interface Code128Symbol {
  /** Alternating bar, space, bar, … widths in modules. */
  widths: number[];
}

export interface EncodedCode128 {
  /** The symbol values in order (start … checksum, stop), for the tests. */
  values: number[];
  /** Bar/space widths in modules, left to right, bars first, quiet zones EXCLUDED. */
  widths: number[];
  /** Total modules across, quiet zones excluded. */
  modules: number;
}

function digitsAhead(text: string, from: number): number {
  let n = 0;
  while (from + n < text.length && text[from + n] >= "0" && text[from + n] <= "9") n += 1;
  return n;
}

/**
 * Encodes printable ASCII (32..126) with code sets B and C. Set C is entered
 * for any run of four or more digits (or an even run that reaches the end),
 * which is what makes a box code short: `BX000017` is nine symbols, not
 * eleven. Throws on anything outside the printable range — a label never
 * carries control characters, and silently dropping one would print a code
 * that scans to the wrong box.
 */
export function encodeCode128(text: string): EncodedCode128 {
  if (text.length === 0) throw new Error("Nothing to encode");
  for (const ch of text) {
    const c = ch.charCodeAt(0);
    if (c < 32 || c > 126) throw new Error(`Code 128 B cannot encode ${JSON.stringify(ch)}`);
  }

  const values: number[] = [];
  let i = 0;
  let set: "B" | "C";
  const leading = digitsAhead(text, 0);
  if (leading >= 4 || (leading >= 2 && leading === text.length)) {
    values.push(START_C);
    set = "C";
  } else {
    values.push(START_B);
    set = "B";
  }

  while (i < text.length) {
    const run = digitsAhead(text, i);
    if (set === "B") {
      if (run >= 4 || (run >= 2 && i + run === text.length)) {
        values.push(CODE_C);
        set = "C";
        continue;
      }
      values.push(text.charCodeAt(i) - 32);
      i += 1;
    } else {
      if (run >= 2) {
        values.push(Number(text.slice(i, i + 2)));
        i += 2;
      } else {
        values.push(CODE_B);
        set = "B";
      }
    }
  }

  let sum = values[0];
  for (let k = 1; k < values.length; k += 1) sum += values[k] * k;
  values.push(sum % 103);
  values.push(STOP);

  const widths: number[] = [];
  for (const v of values) {
    for (const w of PATTERNS[v]) widths.push(Number(w));
  }
  return { values, widths, modules: widths.reduce((a, b) => a + b, 0) };
}

/** Quiet zone each side, in modules — the spec's minimum is 10. */
export const QUIET_ZONE_MODULES = 10;

/**
 * The widest module (in dots) at which the symbol, with quiet zones, fits in
 * `availableDots`. Prefers 3 dots — it scans reliably at 203 dpi from a
 * phone-class camera at arm's length — then 2. Returns 1 only as a last
 * resort, with `tooNarrow` set so a caller can say so on screen rather than
 * print a label that will not read.
 */
export function pickModuleDots(
  encoded: EncodedCode128,
  availableDots: number,
): { dots: number; tooNarrow: boolean } {
  const total = encoded.modules + 2 * QUIET_ZONE_MODULES;
  for (const dots of [3, 2]) {
    if (total * dots <= availableDots) return { dots, tooNarrow: false };
  }
  // One dot is 0.125 mm at 203 dpi, well under what a phone-class camera
  // decodes, so falling to it IS the "too narrow" case this flag exists to
  // report — it used to be raised only when the symbol did not fit at all,
  // which is the one case the caller cannot print anyway. On a 58 mm roll a
  // box code never comes down here; on a narrower one it did, silently.
  return { dots: 1, tooNarrow: true };
}

/**
 * Burns the bars into an existing 1-bit bitmap at integer dot positions.
 * `x` is the LEFT edge of the first bar (the quiet zone is the caller's blank
 * paper). Returns the width consumed, in dots.
 */
export function drawCode128Bars(
  bitmap: { width: number; height: number; pixels: Uint8Array },
  encoded: EncodedCode128,
  at: { x: number; y: number; height: number; moduleDots: number },
): number {
  let cursor = at.x;
  encoded.widths.forEach((w, index) => {
    const px = w * at.moduleDots;
    const isBar = index % 2 === 0;
    if (isBar) {
      for (let yy = at.y; yy < at.y + at.height && yy < bitmap.height; yy += 1) {
        for (let xx = cursor; xx < cursor + px && xx < bitmap.width; xx += 1) {
          if (xx >= 0 && yy >= 0) bitmap.pixels[yy * bitmap.width + xx] = 1;
        }
      }
    }
    cursor += px;
  });
  return cursor - at.x;
}
